/* eslint-disable react/no-danger */
import React, { Children } from 'react';
import PropTypes from 'prop-types';
import { compose, defaultProps, withProps } from 'recompose';
import { connect } from 'react-redux';
import { get } from 'lodash';

import { CoreBreadcrumbs } from 'components/core/library/breadcrumbs';
import Crumb from './Crumb';

import './breadcrumbs.scss';

function createCrumbSchema(crumbs = []) {
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: crumbs
			.filter((crumb) => crumb.props.label)
			.map((crumb, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				item: {
					'@type': index === 0 ? 'WebSite' : 'Thing',
					'@id': get(crumb, 'props.href', '').split('?').shift(),
					name: crumb.props.name || crumb.props.label,
				},
			})),
	};
}

function createCLPBreadcrumbSchema(crumbs = [], baseUrl, isNearMePage = false) {
	const ROOT_URL = baseUrl || 'https://www.sparefoot.com';
	const ensureTrailingSlash = (url) =>
		url && !url.endsWith('/') && !url.endsWith('.html')
			? `${url}/`
			: url;
	const filtered = crumbs.filter((crumb) => crumb?.props?.label);

	const itemListElement = filtered.map((crumb, index) => {
		const rawHref = (get(crumb, 'props.href', '') || '').split('?').shift();
		const isAbsolute = /^https?:\/\//i.test(rawHref);
		const absoluteUrl = !rawHref
			? ROOT_URL
			: isAbsolute
			? rawHref
			: `${ROOT_URL.replace(/\/$/, '')}${
					rawHref.startsWith('/') ? rawHref : `/${rawHref}`
			  }`;
		const idUrl = ensureTrailingSlash(absoluteUrl);

		return {
			'@type': 'ListItem',
			position: index + 1,
			item: {
				'@type':
					index === 0
						? 'WebSite'
						: isNearMePage
						? 'WebPage'
						: 'Thing',
				'@id': idUrl,
				name: crumb.props.name || crumb.props.label,
				url: idUrl,
			},
		};
	});

	const cityCrumb = filtered.find(
		(c) => c?.props?.className === 'sublocality'
	);
	let landingUrl = '';

	if (cityCrumb) {
		const raw = (get(cityCrumb, 'props.href', '') || '').split('?').shift();
		const isAbs = /^https?:\/\//i.test(raw);
		const abs = !raw
			? ROOT_URL
			: isAbs
			? raw
			: `${ROOT_URL.replace(/\/$/, '')}${
					raw.startsWith('/') ? raw : `/${raw}`
			  }`;
		landingUrl = ensureTrailingSlash(abs);
	} else {
		landingUrl =
			get(
				itemListElement,
				`[${itemListElement.length - 1}].item['@id']`,
				''
			) || ensureTrailingSlash(ROOT_URL);
	}

	const breadcrumbListId = `${landingUrl.replace(/#.*$/, '')}#breadcrumb`;

	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		...(!isNearMePage && { '@id': breadcrumbListId }),
		itemListElement,
	};
}

export const enhance = compose(
	connect((state) => ({
		pageType: state?.pageMeta?.pageType,
	})),
	defaultProps({ location: {} }),
	withProps(
		({
			location,
			cityUrl,
			stateUrl,
			crumbs = [],
			baseUrl,
			isNewSearchPage,
			isCLPPage,
			pageType,
		}) => {
			const newCrumbs = [];
			const { fullState, subLocality, city } = location;
			const sub = subLocality || city;
			const isNearMePage = pageType === 'nearMePages';

			newCrumbs.push(
				<Crumb
					href="/"
					key="base"
					label="Home"
					name="Home"
				/>
			);

			if (!isNewSearchPage) {
				newCrumbs.push(
					<Crumb
						href="/storage-101.html"
						key="base"
						label="Find storage"
						name="Find storage"
					/>
				);
			}

			if (stateUrl) {
				newCrumbs.push(
					<Crumb
						key="state"
						href={stateUrl}
						isLast={!cityUrl && !crumbs.length}
						label={fullState}
						className="state"
					/>
				);
			}

			if (cityUrl) {
				newCrumbs.push(
					<Crumb
						key="sublocality"
						className="sublocality"
						href={cityUrl}
						isLast={!crumbs.length}
						label={isCLPPage ? sub : `${sub} self-storage`}
					>
						{sub}{' '}
						{!isNewSearchPage && (
							<span className="self-storage">self-storage</span>
						)}
					</Crumb>
				);
			}

			return {
				crumbs: [...newCrumbs, ...crumbs],
				crumbJSON: isCLPPage
					? createCLPBreadcrumbSchema(
							[...newCrumbs, ...crumbs],
							baseUrl,
							isNearMePage
					  )
					: createCrumbSchema([...newCrumbs, ...crumbs]),
			};
		}
	)
);

export function Breadcrumbs({ crumbs, crumbJSON, className }) {
	return [
		<CoreBreadcrumbs
			key="crumbs"
			className={className}
		>
			{Children.toArray(crumbs)}
		</CoreBreadcrumbs>,
		<script
			key="schema"
			type="application/ld+json"
			dangerouslySetInnerHTML={{
				__html: JSON.stringify(crumbJSON),
			}}
		/>,
	];
}

Breadcrumbs.propTypes = {
	className: PropTypes.string,
	crumbs: PropTypes.node,
};

export default enhance(Breadcrumbs);
